<template>
    <v-container fluid class="t2-header-nav">
        <v-container class="t2-header-nav__bar">
            <v-row align="center" no-gutters>

                <!-- 로고 -->
                <v-col cols="auto" class="t2-header-nav__logo">
                    <router-link :to="currentUserShopSlug ? '/' + currentUserShopSlug : { name: 'Home' }" class="t2-logo">
                        <template v-if="currentCategoryImage">
                            <img :src="currentCategoryImage" :alt="currentCategoryName || appName" class="t2-logo__img" />
                        </template>
                        <template v-else-if="shopLogo">
                            <img :src="shopLogo" :alt="appName" class="t2-logo__img" />
                        </template>
                        <template v-else>
                            <img src="/assets/img/header/header-bi.svg" :alt="appName" class="t2-logo__bi" />
                            <img src="/assets/img/header/header-logo.svg" :alt="appName" class="t2-logo__default" />
                        </template>
                    </router-link>
                </v-col>

                <v-spacer />

                <!-- 우측 영역 -->
                <v-col cols="auto" class="t2-header-nav__right">

                    <!-- 로그인 상태 -->
                    <template v-if="isAuthenticated">

                        <!-- 홈 아이콘 버튼 -->
                        <div v-if="isUseAfterLogin" class="t2-icon-btn">
                            <v-tooltip attach bottom transition="slide-y-transition">
                                <template v-slot:activator="{ on, attrs }">
                                    <router-link to="/after-login" class="t2-icon-inner">
                                        <i v-bind="attrs" v-on="on" class="las la-home fs-22"></i>
                                    </router-link>
                                </template>
                                <span>홈</span>
                            </v-tooltip>
                        </div>

                        <!-- API logobar_menu 아이콘 (장바구니 등) -->
                        <div v-for="(menuLabel, i) in $optional('data.logobar_menu?.labels')" :key="i" class="t2-icon-btn">
                            <v-tooltip attach bottom transition="slide-y-transition">
                                <template v-slot:activator="{ on, attrs }">
                                    <router-link :to="$optional('data.logobar_menu.links')[i]" class="t2-icon-inner">
                                        <i class="las fs-22 position-relative"
                                           v-bind="attrs" v-on="on"
                                           :class="$optional('data.logobar_menu.icons')[i]">
                                            <v-badge
                                                color="error"
                                                :content="getCartCount"
                                                inline
                                                v-if="getCartCount > 0 && $optional('data.logobar_menu.links')[i] === '/cart'"
                                                class="t2-header-badge"
                                            ></v-badge>
                                        </i>
                                    </router-link>
                                </template>
                                <span>{{ menuLabel }}</span>
                            </v-tooltip>
                        </div>

                        <!-- 사용자 정보: 이름(지국)(회사명) -->
                        <div class="t2-user-info">
                            <span class="t2-user-info__name">{{ currentUser.name }}</span>
                            <span v-if="kumonBranch" class="t2-user-info__branch">({{ kumonBranch }})</span>
                            <span v-if="currentUserCompany.company_name" class="t2-user-info__company">({{ currentUserCompany.company_name }})</span>
                        </div>

                        <!-- 로그아웃 -->
                        <v-tooltip attach bottom transition="slide-y-transition">
                            <template v-slot:activator="{ on, attrs }">
                                <router-link :to="{ name: 'Logout' }" class="t2-icon-btn">
                                    <i v-bind="attrs" v-on="on" class="las la-sign-out-alt fs-22"></i>
                                </router-link>
                            </template>
                            <span>로그아웃</span>
                        </v-tooltip>
                    </template>

                    <!-- 비로그인 상태: 로그인 버튼 -->
                    <template v-else>
                        <v-tooltip attach bottom transition="slide-y-transition">
                            <template v-slot:activator="{ on, attrs }">
                                <router-link :to="loginLink" class="t2-icon-btn">
                                    <i v-bind="attrs" v-on="on" class="las la-sign-in-alt fs-22"></i>
                                </router-link>
                            </template>
                            <span>로그인</span>
                        </v-tooltip>
                    </template>
                </v-col>

            </v-row>
        </v-container>
    </v-container>
</template>

<script>
    import { mapGetters } from 'vuex';

    export default {
        name: 'HeaderMenuType2',
        props: {
            data: { type: Object, default: () => ({}) },
        },
        computed: {
            ...mapGetters('cart', ['getCartCount']),
            ...mapGetters('auth', ['isAuthenticated', 'isUseAfterLogin', 'currentUser', 'currentUserCompany', 'currentUserShopSlug', 'kumonBranch', 'guestShopLogo']),
            ...mapGetters('app', ['appName', 'currentCategoryImage', 'currentCategoryName']),
            shopLogo() {
                return this.data?.shop_logo || this.guestShopLogo || this.data?.home_shop_logo || '';
            },
            shopSlug() {
                if (this.currentUserShopSlug) return this.currentUserShopSlug;
                const redirect = this.$route?.query?.redirect || '';
                return redirect.split('/').filter(Boolean)[0] || null;
            },
            loginLink() {
                if (this.shopSlug) {
                    return { name: 'Login', query: { redirect: '/' + this.shopSlug } };
                }
                return { name: 'Login' };
            },
        },
    };
</script>

<style lang="scss" scoped>
$t2-primary: #000000;

.t2-header-nav {
    background: #fff;
    padding: 0;
    height: 100%;

    &__bar {
        height: 100%;
        padding: 0 12px;
    }

    &__logo {
        display: flex;
        align-items: center;
        padding-right: 16px;
    }

    &__right {
        display: flex;
        align-items: center;
        gap: 4px;
    }
}

/* 로고 */
.t2-logo {
    display: flex;
    align-items: center;
    text-decoration: none;

    &__img   { height: 46px; width: auto; object-fit: contain; }
    &__bi    { height: 28px; margin-right: 6px; }
    &__default { height: 46px; }
}

/* 사용자 정보 */
.t2-user-info {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-right: 8px;
    font-size: 13px;
    color: #333;
    white-space: nowrap;

    &__name    { font-weight: 600; color: $t2-primary; }
    &__branch  { color: #555; }
    &__company { color: #555; }
}

/* 아이콘 버튼 */
.t2-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    color: #333;
    text-decoration: none;
    border-radius: 4px;
    transition: background 0.15s;

    &:hover { background: #f5f5f5; color: $t2-primary; }
}

.t2-icon-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: inherit;
    text-decoration: none;
}

/* 장바구니 배지 */
.t2-header-badge {
    position: absolute;
    top: -6px;
    right: -8px;
}
</style>
